import { isResolveSpaceLink, resolveLink } from '@bettermode/common/url'
import { useSpace } from '@tribeplatform/react-sdk/hooks'

import { useFirstRenderContext } from '../common/ssr/index.js'

/**
 * Resolves a resolvable-link token (`space://<id>:<fallback>`) to the space's
 * current relative URL. Non-token hrefs are returned unchanged.
 *
 * Idiomatic per-component resolution: it reads the space via `useSpace`
 * (React Query + `useCachedSpace` placeholder), so already-cached spaces
 * resolve with no request. On first render (SSR + initial client render) it
 * returns the token's embedded fallback path for hydration parity, then
 * upgrades to the live `relativeUrl` after hydration; an unresolved or deleted
 * space also falls back to the embedded path.
 *
 * Declarative consumers just render `<Link href={maybeSpaceToken} />` — `Link`
 * calls this internally. Imperative sinks that read the stored value outside
 * `Link` (e.g. `navigate(href)`, active-link comparisons) use the public
 * `useResolvedHref` alias re-exported from the package barrel.
 */
export const useResolveLink = (
  href: string | undefined,
): string | undefined => {
  const { isFirstRender } = useFirstRenderContext()
  const resolved = href ? resolveLink(href) : undefined
  const spaceLink = isResolveSpaceLink(resolved) ? resolved : null

  const { data: space } = useSpace({
    variables: { id: spaceLink?.spaceId ?? '' },
    fields: 'basic',
    useQueryOptions: { enabled: !!spaceLink },
  })

  if (!spaceLink) {
    return href
  }
  if (isFirstRender) {
    return spaceLink.fallback
  }
  return space?.relativeUrl ?? spaceLink.fallback
}
